import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import MapIcon from '@mui/icons-material/Map';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ReportFilter, { updateReportParams } from './components/ReportFilter';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import ReportsMenu from './components/ReportsMenu';
import PositionValue from '../common/components/PositionValue';
import ColumnSelect from './components/ColumnSelect';
import usePositionAttributes from '../common/attributes/usePositionAttributes';
import { useCatch } from '../reactHelper';
import MapView from '../map/core/MapView';
import MapRoutePath from '../map/MapRoutePath';
import MapRoutePoints from '../map/MapRoutePoints';
import MapPositions from '../map/MapPositions';
import useReportStyles from './common/useReportStyles';
import TableShimmer from '../common/components/TableShimmer';
import MapCamera from '../map/MapCamera';
import MapGeofence from '../map/MapGeofence';
import scheduleReport from './common/scheduleReport';
import MapScale from '../map/MapScale';
import { useRestriction } from '../common/util/permissions';
import CollectionActions from '../settings/components/CollectionActions';
import fetchOrThrow from '../common/util/fetchOrThrow';
import SelectField from '../common/components/SelectField';
import exportExcel, { startNativeFileDownload } from '../common/util/exportExcel';
import ExportFallbackActions from './components/ExportFallbackActions';

const STORAGE_KEY_COLUMNS = 'positionsReportColumns';
const VIRTUAL_DEVICE_NAME_COLUMN = 'deviceName';
const PRIMARY_TIME_FIELD = 'serverTime';

const EXCLUDED_COLUMNS = ['id', 'deviceId', 'outdated', 'network', 'attributes', 'fixTime'];

const PREFERRED_DEFAULT_COLUMNS = [
  VIRTUAL_DEVICE_NAME_COLUMN,
  PRIMARY_TIME_FIELD,
  'speed',
  'address',
  'ignition',
  'totalDistance',
  'driverUniqueId',
];

const timeValue = (position) => {
  const raw = position?.[PRIMARY_TIME_FIELD]
    || position?.fixTime
    || position?.deviceTime
    || position?.attributes?.[PRIMARY_TIME_FIELD]
    || 0;

  const timestamp = new Date(raw).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const PositionsReportPage = () => {
  const navigate = useNavigate();
  const { classes } = useReportStyles();
  const t = useTranslation();
  const theme = useTheme();

  const isPhone = useMediaQuery('(max-width:599px)');
  const isTablet = useMediaQuery('(min-width:600px) and (max-width:1023px)');
  const isTv = useMediaQuery('(min-width:1600px)');
  const isTouchViewport = isPhone || isTablet;

  const [searchParams, setSearchParams] = useSearchParams();

  const positionAttributes = usePositionAttributes(t);
  const readonly = useRestriction('readonly');

  const [available, setAvailable] = useState([]);
  const [columns, setColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_COLUMNS);
      const parsed = saved ? JSON.parse(saved) : PREFERRED_DEFAULT_COLUMNS;
      return Array.isArray(parsed) && parsed.length ? parsed : PREFERRED_DEFAULT_COLUMNS;
    } catch {
      return PREFERRED_DEFAULT_COLUMNS;
    }
  });
  const [items, setItems] = useState([]);
  const [deviceNames, setDeviceNames] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [dragPosition, setDragPosition] = useState(null);
  const [exportUrl, setExportUrl] = useState('');
  const [exportMessage, setExportMessage] = useState({ open: false, severity: 'info', text: '' });

  const selectedRowRef = useRef(null);
  const bodyScrollPositionRef = useRef(0);
  const selectionOriginRef = useRef('table');
  const tableScrollRef = useRef(null);

  const geofenceParam = searchParams.get('geofenceId');
  const geofenceId = geofenceParam && !Number.isNaN(Number(geofenceParam))
    ? parseInt(geofenceParam, 10)
    : null;

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_COLUMNS, JSON.stringify(columns));
    } catch {
      // ignora erro de storage
    }
  }, [columns]);

  useEffect(() => {
    if (!selectedItem || selectionOriginRef.current !== 'map' || !selectedRowRef.current) {
      return;
    }

    selectedRowRef.current.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: 'smooth',
    });
  }, [selectedItem]);

  useEffect(() => {
    const styleId = 'positions-report-print-style';

    if (document.getElementById(styleId)) {
      return undefined;
    }

    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      @media print {
        html, body {
          height: auto !important;
          overflow: visible !important;
        }

        .MuiDialog-root,
        .MuiPopover-root,
        .MuiPopper-root {
          display: none !important;
        }

        .${classes.container} {
          display: block !important;
          height: auto !important;
          overflow: visible !important;
        }

        .${classes.containerMain} {
          display: block !important;
          height: auto !important;
          overflow: visible !important;
        }

        .${classes.header} {
          display: none !important;
        }

        .${classes.containerMap} {
          display: none !important;
        }

        table {
          width: 100% !important;
          table-layout: auto !important;
          border-collapse: collapse !important;
        }

        thead {
          display: table-header-group !important;
        }

        tfoot {
          display: table-footer-group !important;
        }

        tr, td, th {
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }

        .${classes.columnAction},
        .${classes.actionCellPadding},
        .positionsReportColumnDragHandle,
        .positionsReportExpandedBackdrop {
          display: none !important;
        }
      }

      .positionsReportTableCard {
        overflow: hidden;
      }

      .positionsReportTableScroller {
        scrollbar-gutter: stable both-edges;
      }

      @media print {
        .positionsReportTableCard,
        .positionsReportTableScroller {
          overflow: visible !important;
          max-height: none !important;
          height: auto !important;
          visibility: visible !important;
          pointer-events: auto !important;
        }
      }

      .positionsReportTableScroller::-webkit-scrollbar {
        height: 14px;
        width: 14px;
      }

      .positionsReportTableScroller::-webkit-scrollbar-thumb {
        background: rgba(100, 116, 139, 0.55);
        border-radius: 999px;
        border: 3px solid rgba(255, 255, 255, 0.96);
      }

      .positionsReportTableScroller::-webkit-scrollbar-track {
        background: rgba(241, 245, 249, 0.9);
        border-radius: 999px;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const existing = document.getElementById(styleId);
      if (existing) {
        existing.remove();
      }
    };
  }, [classes]);

  useEffect(() => {
    if (!mapExpanded) {
      return undefined;
    }

    bodyScrollPositionRef.current = window.scrollY;

    const originalBody = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      overscrollBehavior: document.body.style.overscrollBehavior,
    };

    const originalHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${bodyScrollPositionRef.current}px`;
    document.body.style.width = '100%';
    document.body.style.overscrollBehavior = 'contain';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalBody.overflow;
      document.body.style.position = originalBody.position;
      document.body.style.top = originalBody.top;
      document.body.style.width = originalBody.width;
      document.body.style.overscrollBehavior = originalBody.overscrollBehavior;
      document.documentElement.style.overflow = originalHtmlOverflow;
      window.scrollTo({ top: bodyScrollPositionRef.current, behavior: 'auto' });
    };
  }, [mapExpanded]);

  const groupedPositions = useMemo(() => {
    const grouped = new Map();

    items.forEach((position) => {
      if (!grouped.has(position.deviceId)) {
        grouped.set(position.deviceId, []);
      }

      grouped.get(position.deviceId).push(position);
    });

    return Array.from(grouped.entries()).map(([deviceId, positions]) => ({
      deviceId,
      positions: [...positions].sort((a, b) => timeValue(a) - timeValue(b)),
    }));
  }, [items]);

  const cameraPositions = useMemo(() => (
    selectedItem ? [selectedItem] : items
  ), [selectedItem, items]);

  const viewportConfig = useMemo(() => {
    if (isPhone) {
      return {
        pageGap: 12,
        mapHeight: 240,
        mapMinHeight: 220,
        mapMaxHeight: 300,
        mapExpandedMinHeight: 'calc(100vh - 16px)',
        mapExpandedMaxHeight: 'calc(100vh - 16px)',
        mapExpandedInset: 8,
        mapExpandedRadius: 14,
        mapNormalRadius: 12,
        cardRadius: 14,
        tableMinWidth: 760,
        cellPadding: '10px 10px',
        headerMinWidth: 130,
      };
    }

    if (isTablet) {
      return {
        pageGap: 14,
        mapHeight: 300,
        mapMinHeight: 280,
        mapMaxHeight: 380,
        mapExpandedMinHeight: 420,
        mapExpandedMaxHeight: 'calc(100vh - 24px)',
        mapExpandedInset: 12,
        mapExpandedRadius: 16,
        mapNormalRadius: 14,
        cardRadius: 16,
        tableMinWidth: 980,
        cellPadding: '11px 12px',
        headerMinWidth: 150,
      };
    }

    if (isTv) {
      return {
        pageGap: 20,
        mapHeight: 420,
        mapMinHeight: 420,
        mapMaxHeight: 560,
        mapExpandedMinHeight: 560,
        mapExpandedMaxHeight: '92vh',
        mapExpandedInset: 24,
        mapExpandedRadius: 24,
        mapNormalRadius: 20,
        cardRadius: 22,
        tableMinWidth: 1600,
        cellPadding: '16px 18px',
        headerMinWidth: 210,
      };
    }

    return {
      pageGap: 16,
      mapHeight: 340,
      mapMinHeight: 320,
      mapMaxHeight: 440,
      mapExpandedMinHeight: 460,
      mapExpandedMaxHeight: '84vh',
      mapExpandedInset: 16,
      mapExpandedRadius: 18,
      mapNormalRadius: 16,
      cardRadius: 18,
      tableMinWidth: 1100,
      cellPadding: '12px 14px',
      headerMinWidth: 165,
    };
  }, [isPhone, isTablet, isTv]);

  const handleToggleMapExpanded = useCallback(() => {
    setMapExpanded((prev) => !prev);
  }, []);

  const handleSelectItem = useCallback((item) => {
    selectionOriginRef.current = 'table';
    setSelectedItem(item);
  }, []);

  const handleClearSelectedItem = useCallback(() => {
    setSelectedItem(null);
    selectionOriginRef.current = 'table';
  }, []);

  const onMapPointClick = useCallback(
    (positionId) => {
      const found = items.find((it) => it.id === positionId) || null;
      selectionOriginRef.current = 'map';
      setSelectedItem(found);
    },
    [items],
  );

  const moveColumn = useCallback((list, fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= list.length || toIndex > list.length) {
      return list;
    }

    const next = [...list];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next;
  }, []);

  const finishColumnDrag = useCallback((targetKey, dropPosition) => {
    if (!draggedColumn || !targetKey || draggedColumn === targetKey) {
      setDraggedColumn(null);
      setDragOverColumn(null);
      setDragPosition(null);
      return;
    }

    setColumns((prevColumns) => {
      const fromIndex = prevColumns.indexOf(draggedColumn);
      const targetIndex = prevColumns.indexOf(targetKey);

      if (fromIndex === -1 || targetIndex === -1) {
        return prevColumns;
      }

      const insertIndex = dropPosition === 'before' ? targetIndex : targetIndex + 1;
      const adjustedIndex = fromIndex < insertIndex ? insertIndex - 1 : insertIndex;

      return moveColumn(prevColumns, fromIndex, adjustedIndex);
    });

    setDraggedColumn(null);
    setDragOverColumn(null);
    setDragPosition(null);
  }, [draggedColumn, moveColumn]);

  const handleColumnDragStart = useCallback((event, key) => {
    if (isTouchViewport) {
      return;
    }

    setDraggedColumn(key);
    setDragOverColumn(key);
    setDragPosition('after');

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', key);
    }
  }, [isTouchViewport]);

  const handleColumnDragOver = useCallback((event, key) => {
    if (isTouchViewport) {
      return;
    }

    event.preventDefault();

    const rect = event.currentTarget.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const nextPosition = offsetX < rect.width / 2 ? 'before' : 'after';

    if (dragOverColumn !== key || dragPosition !== nextPosition) {
      setDragOverColumn(key);
      setDragPosition(nextPosition);
    }

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }, [dragOverColumn, dragPosition, isTouchViewport]);

  const handleColumnDrop = useCallback((event, key) => {
    if (isTouchViewport) {
      return;
    }

    event.preventDefault();
    finishColumnDrag(key, dragPosition || 'after');
  }, [dragPosition, finishColumnDrag, isTouchViewport]);

  const handleColumnDragEnd = useCallback(() => {
    setDraggedColumn(null);
    setDragOverColumn(null);
    setDragPosition(null);
  }, []);

  const buildQuery = useCallback(({ deviceIds, from, to }) => {
    const query = new URLSearchParams({ from, to });

    if (geofenceId) {
      query.append('geofenceId', geofenceId);
    }

    (deviceIds || []).forEach((deviceId) => query.append('deviceId', deviceId));

    return query;
  }, [geofenceId]);

  const resolveDeviceName = useCallback((position, namesMap = deviceNames) => (
    namesMap[position?.deviceId]
    || position?.deviceName
    || position?.name
    || position?.attributes?.deviceName
    || String(position?.deviceId ?? '')
  ), [deviceNames]);

  const fetchDeviceNames = useCallback(async (deviceIds) => {
    const uniqueDeviceIds = [...new Set((deviceIds || []).filter(Boolean))];

    if (!uniqueDeviceIds.length) {
      return {};
    }

    const query = new URLSearchParams();
    uniqueDeviceIds.forEach((deviceId) => query.append('id', deviceId));

    try {
      const response = await fetchOrThrow(`/api/devices?${query.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      const data = await response.json();

      return data.reduce((acc, device) => {
        acc[device.id] = device.name || String(device.id);
        return acc;
      }, {});
    } catch {
      return {};
    }
  }, []);

  const buildFriendlyHeader = useCallback((key) => {
    if (key === VIRTUAL_DEVICE_NAME_COLUMN) {
      return t('sharedDevice');
    }

    const metadata = positionAttributes[key];
    if (metadata?.name) {
      return metadata.name;
    }

    return key
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }, [positionAttributes, t]);

  const notifyExport = useCallback((text, severity = 'error') => {
    setExportMessage({
      open: true,
      severity,
      text,
    });
  }, []);

  const closeExportMessage = useCallback(() => {
    setExportMessage((prev) => ({ ...prev, open: false }));
  }, []);

  const validateExportUrl = useCallback(async (url) => {
    try {
      const response = await fetch(url, {
        method: 'HEAD',
        headers: { Accept: 'text/csv,application/octet-stream' },
      });

      return response.ok;
    } catch {
      return false;
    }
  }, []);

  const exportAsExcelFallback = useCallback(async (deviceIds, from, to) => {
    const query = buildQuery({ deviceIds, from, to });
    const [response, namesMap] = await Promise.all([
      fetchOrThrow(`/api/positions?${query.toString()}`, {
        headers: { Accept: 'application/json' },
      }),
      fetchDeviceNames(deviceIds),
    ]);

    const exportItems = await response.json();
    const headers = columns;
    const sheetRows = exportItems.map((item) => {
      const row = {};
      headers.forEach((key) => {
        const header = key === VIRTUAL_DEVICE_NAME_COLUMN ? t('sharedDevice') : buildFriendlyHeader(key);
        const value = key === VIRTUAL_DEVICE_NAME_COLUMN
          ? resolveDeviceName(item, namesMap)
          : (Object.prototype.hasOwnProperty.call(item, key)
            ? item[key]
            : item?.attributes?.[key]);
        row[header] = value === null || value === undefined ? '' : String(value);
      });
      return row;
    });

    await exportExcel(t('reportPositions'), 'positions.xlsx', new Map([[t('reportPositions'), sheetRows]]), theme);
  }, [buildFriendlyHeader, buildQuery, columns, fetchDeviceNames, resolveDeviceName, t, theme]);

  const buildHeaderIcon = useCallback((key) => {
    const normalized = String(key).toLowerCase();

    if (normalized.includes('time') || normalized.includes('date')) {
      return <AccessTimeIcon style={{ fontSize: 14 }} />;
    }

    if (normalized.includes('speed') || normalized.includes('distance') || normalized.includes('odometer')) {
      return <DirectionsCarIcon style={{ fontSize: 14 }} />;
    }

    return <ViewColumnIcon style={{ fontSize: 14 }} />;
  }, []);

  const prepareAvailableColumns = useCallback((data) => {
    const detectedKeys = new Set();

    data.forEach((position) => {
      Object.keys(position || {}).forEach((it) => detectedKeys.add(it));
      Object.keys(position?.attributes || {}).forEach((it) => detectedKeys.add(it));
    });

    EXCLUDED_COLUMNS.forEach((key) => detectedKeys.delete(key));
    detectedKeys.delete(VIRTUAL_DEVICE_NAME_COLUMN);

    const attributeKeys = Object.keys(positionAttributes).filter((key) => detectedKeys.has(key));
    const preferredKeys = PREFERRED_DEFAULT_COLUMNS.filter(
      (key) => key !== VIRTUAL_DEVICE_NAME_COLUMN && detectedKeys.has(key),
    );
    const remainingKeys = Array.from(detectedKeys).filter(
      (key) => !attributeKeys.includes(key) && !preferredKeys.includes(key),
    );

    const orderedKeys = Array.from(new Set([
      VIRTUAL_DEVICE_NAME_COLUMN,
      ...preferredKeys,
      ...attributeKeys,
      ...remainingKeys,
    ]));

    const availableColumnsSet = new Set(orderedKeys);

    setAvailable(orderedKeys.map((key) => [key, buildFriendlyHeader(key)]));

    setColumns((prevColumns) => {
      const previousValidColumns = (Array.isArray(prevColumns) ? prevColumns : [])
        .filter((key) => availableColumnsSet.has(key));

      if (previousValidColumns.length) {
        return previousValidColumns;
      }

      const preferredAvailableColumns = PREFERRED_DEFAULT_COLUMNS
        .filter((key) => availableColumnsSet.has(key));

      if (preferredAvailableColumns.length) {
        return preferredAvailableColumns;
      }

      return orderedKeys.slice(0, 7);
    });

    return orderedKeys;
  }, [buildFriendlyHeader, positionAttributes]);

  const onShow = useCatch(async ({ deviceIds, from, to }) => {
    const query = buildQuery({ deviceIds, from, to });

    setLoading(true);

    try {
      const [positionsResponse, namesMap] = await Promise.all([
        fetchOrThrow(`/api/positions?${query.toString()}`, {
          headers: { Accept: 'application/json' },
        }).then((response) => response.json()),
        fetchDeviceNames(deviceIds),
      ]);

      prepareAvailableColumns(positionsResponse);
      setDeviceNames(namesMap);
      setItems(positionsResponse);
      setSelectedItem(null);
      setMapExpanded(false);
      selectionOriginRef.current = 'table';
    } finally {
      setLoading(false);
    }
  });

  const onExport = useCatch(async ({ deviceIds, from, to }) => {
    const query = buildQuery({ deviceIds, from, to });
    const url = `/api/positions/csv?${query.toString()}`;
    const isValidUrl = await validateExportUrl(url);

    if (isValidUrl) {
      setExportUrl(url);
      startNativeFileDownload(url);
      return;
    }

    setExportUrl('');

    try {
      await exportAsExcelFallback(deviceIds, from, to);
      notifyExport('O arquivo CSV não está disponível no momento. Geramos um arquivo XLSX automaticamente.', 'info');
    } catch {
      notifyExport('Não foi possível exportar agora. Tente novamente em alguns instantes.', 'error');
    }
  });

  const onSchedule = useCatch(async (deviceIds, groupIds, report) => {
    await scheduleReport(deviceIds, groupIds, {
      ...report,
      type: 'positions',
    });
    navigate('/reports/scheduled');
  });

  const renderMap = (expanded = false) => {
    const inset = viewportConfig.mapExpandedInset;
    const mapInteractive = expanded || !isTouchViewport;

    return (
      <Fragment>
        {expanded && (
          <div
            className="positionsReportExpandedBackdrop"
            onClick={handleToggleMapExpanded}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.32)',
              backdropFilter: 'blur(3px)',
              WebkitBackdropFilter: 'blur(3px)',
              zIndex: theme.zIndex.modal,
            }}
          />
        )}

        <div
          className={classes.containerMap}
          style={{
            height: expanded ? (isPhone ? 'calc(100vh - 16px)' : isTablet ? 'calc(100vh - 24px)' : '74vh') : viewportConfig.mapHeight,
            minHeight: expanded ? viewportConfig.mapExpandedMinHeight : viewportConfig.mapMinHeight,
            maxHeight: expanded ? viewportConfig.mapExpandedMaxHeight : viewportConfig.mapMaxHeight,
            position: expanded ? 'fixed' : 'relative',
            top: expanded ? inset : 'auto',
            left: expanded ? inset : 'auto',
            right: expanded ? inset : 'auto',
            bottom: expanded ? inset : 'auto',
            inset: expanded ? `${inset}px` : 'auto',
            zIndex: expanded ? theme.zIndex.modal + 1 : 'auto',
            background: '#fff',
            borderRadius: expanded ? viewportConfig.mapExpandedRadius : viewportConfig.mapNormalRadius,
            overflow: 'hidden',
            boxShadow: expanded
              ? '0 24px 80px rgba(15, 23, 42, 0.24)'
              : '0 10px 32px rgba(15, 23, 42, 0.10)',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            overscrollBehavior: expanded ? 'contain' : 'auto',
            WebkitOverflowScrolling: expanded ? 'touch' : 'auto',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: mapInteractive ? 'auto' : 'none',
            }}
          >
            <MapView>
              <MapGeofence />
              {groupedPositions.map(({ deviceId, positions }) => (
                <Fragment key={deviceId}>
                  <MapRoutePath positions={positions} />
                  <MapRoutePoints positions={positions} onClick={onMapPointClick} />
                </Fragment>
              ))}
              {selectedItem && (
                <MapPositions positions={[selectedItem]} titleField={PRIMARY_TIME_FIELD} />
              )}
            </MapView>

            <MapScale />
            <MapCamera
              key={selectedItem ? `selected-${selectedItem.id}` : `all-${items.length}`}
              positions={cameraPositions}
            />
          </div>

          <div
            style={{
              position: 'absolute',
              left: isPhone ? 8 : 12,
              right: isPhone ? 8 : 'auto',
              bottom: isPhone ? 8 : 12,
              zIndex: expanded ? theme.zIndex.modal + 2 : theme.zIndex.drawer - 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: isPhone ? 'space-between' : 'flex-start',
              gap: 8,
              background: 'rgba(255, 255, 255, 0.96)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(15, 23, 42, 0.08)',
              borderRadius: 999,
              padding: isPhone ? '8px 10px' : '8px 10px 8px 12px',
              boxShadow: '0 12px 30px rgba(15, 23, 42, 0.16)',
              maxWidth: isPhone ? 'calc(100% - 16px)' : 'unset',
              pointerEvents: 'auto',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: isTv ? 13 : 12,
                fontWeight: 700,
                color: '#334155',
                paddingLeft: 2,
                minWidth: 0,
              }}
            >
              <MapIcon style={{ fontSize: isTv ? 18 : 16 }} />
              <span
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {t('reportPositions')}
              </span>
            </div>

            <div
              style={{
                width: 1,
                height: 18,
                background: 'rgba(15, 23, 42, 0.10)',
                marginLeft: 2,
                marginRight: 2,
                display: isPhone ? 'none' : 'block',
              }}
            />

            {selectedItem && !expanded && (
              <Tooltip title="Limpar seleção">
                <IconButton
                  size="small"
                  onClick={handleClearSelectedItem}
                  sx={{
                    width: isTv ? 36 : 32,
                    height: isTv ? 36 : 32,
                    backgroundColor: 'rgba(15, 23, 42, 0.04)',
                    '&:hover': {
                      backgroundColor: 'rgba(15, 23, 42, 0.08)',
                    },
                  }}
                >
                  <GpsFixedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title={expanded ? t('sharedCollapse') : t('sharedExpand')}>
              <IconButton
                size="small"
                onClick={handleToggleMapExpanded}
                sx={{
                  width: isTv ? 36 : 32,
                  height: isTv ? 36 : 32,
                  backgroundColor: 'rgba(15, 23, 42, 0.04)',
                  '&:hover': {
                    backgroundColor: 'rgba(15, 23, 42, 0.08)',
                  },
                }}
              >
                {expanded ? (
                  <CloseFullscreenIcon fontSize="small" />
                ) : (
                  <OpenInFullIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </Fragment>
    );
  };

  const pageSectionGap = viewportConfig.pageGap;
  const shouldShowMap = Boolean(selectedItem || mapExpanded);

  const tableMaxHeight = mapExpanded
    ? 'none'
    : shouldShowMap
      ? (isPhone ? '44vh' : isTablet ? '48vh' : '54vh')
      : (isPhone ? '58vh' : isTablet ? '62vh' : '68vh');

  const tableCardStyle = {
    border: '1px solid rgba(15, 23, 42, 0.08)',
    borderRadius: viewportConfig.cardRadius,
    overflow: 'hidden',
    background: '#fff',
    boxShadow: '0 14px 40px rgba(15, 23, 42, 0.06)',
  };

  const tableShellStyle = {
    overflow: 'auto',
    maxWidth: '100%',
    maxHeight: tableMaxHeight,
    background: 'linear-gradient(180deg, rgba(248,250,252,0.72) 0%, rgba(255,255,255,1) 100%)',
    WebkitOverflowScrolling: 'touch',
    overscrollBehavior: 'contain',
    ...(mapExpanded && {
      maxHeight: 0,
      overflow: 'hidden',
      visibility: 'hidden',
      pointerEvents: 'none',
    }),
  };

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={['reportTitle', 'reportPositions']}>
      <div
        className={classes.container}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: pageSectionGap,
          minWidth: 0,
        }}
      >
        {shouldShowMap && renderMap(mapExpanded)}

        <div
          className={classes.containerMain}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: pageSectionGap,
            minWidth: 0,
            overflow: mapExpanded ? 'hidden' : 'visible',
          }}
        >
          <div className={classes.header}>
            <ReportFilter
              onShow={onShow}
              onExport={onExport}
              onSchedule={onSchedule}
              deviceType="single"
              loading={loading}
            >
              <div className={classes.filterItem}>
                <SelectField
                  value={geofenceId}
                  onChange={(e) => {
                    const values = e.target.value ? [e.target.value] : [];
                    updateReportParams(searchParams, setSearchParams, 'geofenceId', values);
                  }}
                  endpoint="/api/geofences"
                  label={t('sharedGeofence')}
                  fullWidth
                />
              </div>

              <ColumnSelect
                columns={columns}
                setColumns={setColumns}
                columnsArray={available}
                rawValues
                disabled={!items.length}
              />
            </ReportFilter>
            <ExportFallbackActions exportUrl={exportUrl} onClear={() => setExportUrl('')} />
          </div>

          <div className={`positionsReportTableCard ${classes.tableCard}`} style={tableCardStyle}>
            <div
              ref={tableScrollRef}
              className={`positionsReportTableScroller ${classes.tableScroller}`}
              style={tableShellStyle}
              tabIndex={0}
            >
              <Table
                stickyHeader
                sx={{
                  minWidth: viewportConfig.tableMinWidth,
                  '& .MuiTableCell-root': {
                    borderBottom: '1px solid rgba(226, 232, 240, 0.75)',
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      '& .MuiTableCell-root': {
                        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                        position: mapExpanded ? 'static' : 'sticky',
                        top: mapExpanded ? 'auto' : 0,
                        zIndex: 2,
                      },
                    }}
                  >
                    <TableCell
                      className={classes.columnAction}
                      sx={{
                        width: isPhone ? 48 : 52,
                        minWidth: isPhone ? 48 : 52,
                        zIndex: 3,
                      }}
                    />
                    {columns.map((key) => {
                      const isDragged = draggedColumn === key;
                      const isOver = dragOverColumn === key && draggedColumn !== key;
                      const isBefore = isOver && dragPosition === 'before';
                      const isAfter = isOver && dragPosition === 'after';
                      const canDrag = !isTouchViewport;

                      return (
                        <TableCell
                          key={key}
                          onDragOver={(event) => handleColumnDragOver(event, key)}
                          onDrop={(event) => handleColumnDrop(event, key)}
                          sx={{
                            padding: isPhone ? '8px 8px' : '10px 12px',
                            minWidth: viewportConfig.headerMinWidth,
                            position: mapExpanded ? 'static' : 'sticky',
                            top: mapExpanded ? 'auto' : 0,
                            borderLeft: isBefore ? '3px solid #2563eb' : undefined,
                            borderRight: isAfter ? '3px solid #2563eb' : undefined,
                            transition: 'all 0.18s ease',
                            backgroundColor: isOver ? 'rgba(37, 99, 235, 0.04)' : undefined,
                            zIndex: 2,
                          }}
                        >
                          <div
                            draggable={canDrag}
                            onDragStart={(event) => handleColumnDragStart(event, key)}
                            onDragEnd={handleColumnDragEnd}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: isPhone ? 8 : 10,
                              cursor: canDrag ? 'grab' : 'default',
                              userSelect: 'none',
                              borderRadius: isPhone ? 12 : 14,
                              padding: isPhone ? '8px 9px' : isTv ? '12px 14px' : '10px 12px',
                              border: isDragged
                                ? '1px solid rgba(37, 99, 235, 0.30)'
                                : '1px solid rgba(148, 163, 184, 0.16)',
                              background: isDragged
                                ? 'linear-gradient(180deg, rgba(219,234,254,0.90) 0%, rgba(239,246,255,0.95) 100%)'
                                : 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.92) 100%)',
                              boxShadow: isDragged
                                ? '0 14px 28px rgba(37, 99, 235, 0.16)'
                                : '0 4px 12px rgba(15, 23, 42, 0.04)',
                              transform: isDragged ? 'scale(1.02)' : 'scale(1)',
                              opacity: isDragged ? 0.86 : 1,
                              transition: 'all 0.18s ease',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: isPhone ? 7 : 8,
                                minWidth: 0,
                                flex: 1,
                              }}
                            >
                              <div
                                style={{
                                  width: isTv ? 30 : isPhone ? 24 : 26,
                                  height: isTv ? 30 : isPhone ? 24 : 26,
                                  minWidth: isTv ? 30 : isPhone ? 24 : 26,
                                  borderRadius: isPhone ? 8 : 9,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'rgba(37, 99, 235, 0.10)',
                                  color: '#2563eb',
                                }}
                              >
                                {buildHeaderIcon(key)}
                              </div>

                              <span
                                style={{
                                  fontSize: isTv ? 14 : isPhone ? 12 : 13,
                                  fontWeight: 700,
                                  color: '#0f172a',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {buildFriendlyHeader(key)}
                              </span>
                            </div>

                            {!isTouchViewport && (
                              <div
                                className="positionsReportColumnDragHandle"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: isDragged ? '#2563eb' : '#94a3b8',
                                  transition: 'color 0.18s ease',
                                  flexShrink: 0,
                                }}
                              >
                                <DragIndicatorIcon fontSize="small" />
                              </div>
                            )}
                          </div>
                        </TableCell>
                      );
                    })}
                    <TableCell
                      className={classes.columnAction}
                      sx={{
                        width: isPhone ? 56 : 60,
                        minWidth: isPhone ? 56 : 60,
                        zIndex: 3,
                      }}
                    />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!loading ? (
                    items.map((item, rowIndex) => {
                      const isSelected = selectedItem?.id === item.id;

                      return (
                        <TableRow
                          key={item.id}
                          ref={isSelected ? selectedRowRef : null}
                          selected={isSelected}
                          sx={{
                            '&:hover .positionsRowActionCell': {
                              backgroundColor: 'rgba(248, 250, 252, 0.95)',
                            },
                            '& .MuiTableCell-root': {
                              backgroundColor: isSelected
                                ? 'rgba(219, 234, 254, 0.36)'
                                : rowIndex % 2 === 0
                                  ? '#ffffff'
                                  : '#fcfdff',
                              transition: 'background-color 0.18s ease',
                            },
                            '&:hover .MuiTableCell-root': {
                              backgroundColor: isSelected
                                ? 'rgba(219, 234, 254, 0.46)'
                                : 'rgba(248, 250, 252, 0.92)',
                            },
                          }}
                        >
                          <TableCell
                            className={`${classes.columnAction} positionsRowActionCell`}
                            padding="none"
                            sx={{
                              width: isPhone ? 48 : 52,
                              minWidth: isPhone ? 48 : 52,
                            }}
                          >
                            {isSelected ? (
                              <IconButton
                                size="small"
                                onClick={handleClearSelectedItem}
                                sx={{ touchAction: 'manipulation' }}
                              >
                                <GpsFixedIcon fontSize="small" />
                              </IconButton>
                            ) : (
                              <IconButton
                                size="small"
                                onClick={() => handleSelectItem(item)}
                                sx={{ touchAction: 'manipulation' }}
                              >
                                <LocationSearchingIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>

                          {columns.map((key) => (
                            <TableCell
                              key={key}
                              sx={{
                                padding: viewportConfig.cellPadding,
                                verticalAlign: 'top',
                                fontSize: isTv ? 14 : isPhone ? 12 : 13,
                                color: '#1e293b',
                                maxWidth: isPhone ? 200 : isTablet ? 280 : isTv ? 420 : 320,
                                wordBreak: 'break-word',
                              }}
                            >
                              {key === VIRTUAL_DEVICE_NAME_COLUMN ? (
                                <div
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    fontWeight: 700,
                                    color: '#0f172a',
                                    maxWidth: '100%',
                                  }}
                                >
                                  <div
                                    style={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: 999,
                                      background: isSelected ? '#1d4ed8' : '#2563eb',
                                      boxShadow: isSelected
                                        ? '0 0 0 4px rgba(29, 78, 216, 0.12)'
                                        : '0 0 0 4px rgba(37, 99, 235, 0.10)',
                                      flexShrink: 0,
                                    }}
                                  />
                                  <span
                                    style={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    {resolveDeviceName(item)}
                                  </span>
                                </div>
                              ) : (
                                <PositionValue
                                  position={item}
                                  property={Object.prototype.hasOwnProperty.call(item, key) ? key : null}
                                  attribute={Object.prototype.hasOwnProperty.call(item, key) ? null : key}
                                />
                              )}
                            </TableCell>
                          ))}

                          <TableCell
                            className={`${classes.actionCellPadding} positionsRowActionCell`}
                            sx={{
                              width: isPhone ? 56 : 60,
                              minWidth: isPhone ? 56 : 60,
                            }}
                          >
                            <CollectionActions
                              itemId={item.id}
                              endpoint="positions"
                              readonly={readonly}
                              setTimestamp={() => {
                                setItems((prev) => prev.filter((position) => position.id !== item.id));
                                setSelectedItem((prev) => (prev?.id === item.id ? null : prev));
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableShimmer columns={columns.length + 2} startAction />
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={exportMessage.open}
        autoHideDuration={5000}
        onClose={closeExportMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeExportMessage}
          severity={exportMessage.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {exportMessage.text}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default PositionsReportPage;
